import React from 'react';
import InputField from '../ui-elements/InputField';
import Checkbox from '../ui-elements/Checkbox';
import ActionButton from '../ui-elements/ActionButton';
import SecondaryButton from '../ui-elements/SecondaryButton';
import TermsModal from './TermsModal';
import Utils from '../../data/Utils';

const MONTHLY_SUBSCRIPTION_PRICE_USD = 9;

const bootstrap = global.bootstrap;

class SignUpFields extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      fullName: '',
      emailAddress: '',
      password: '',
      confirmPassword: '',
      isFormValid: false,
      termsAccepted: false,
      isSubmitting: false
    };

    this.errorAlert = React.createRef();
    this.termsModalRef = React.createRef();
    this.bootstrapTermsModal = null;

    this.updateValues = this.updateValues.bind(this);
    this.signupSubmit = this.signupSubmit.bind(this);
    this.showError = this.showError.bind(this);
    this.nameValid = this.nameValid.bind(this);
    this.confirmPasswordValid = this.confirmPasswordValid.bind(this);
    this.onTermsCheckboxChange = this.onTermsCheckboxChange.bind(this);
    this.showTermsModal = this.showTermsModal.bind(this);
    this.acceptTerms = this.acceptTerms.bind(this);
    this.hideTermsModal = this.hideTermsModal.bind(this);
    this.onTermsModalHidden = this.onTermsModalHidden.bind(this);
    this.cleanupTermsModalDom = this.cleanupTermsModalDom.bind(this);
    this.setSubmitting = this.setSubmitting.bind(this);
  }

  setSubmitting(isSubmitting) {
    this.setState({ isSubmitting });
  }

  updateValues(event) {
    const stateKey = event.target.getAttribute('statekey');
    this.setState({ [stateKey]: event.target.value }, this.validateForm);
  }

  nameValid(name) {
    return name.length > 0;
  }

  confirmPasswordValid(confirmPassword) {
    return confirmPassword === this.state.password;
  }

  validateForm() {
    const valid = this.nameValid(this.state.fullName) > 0 &&
                  Utils.isValidEmail(this.state.emailAddress) &&
                  Utils.isValidPassword(this.state.password) &&
                  this.confirmPasswordValid(this.state.confirmPassword);
    this.setState({
      isFormValid: valid
    });
  }

  signupSubmit() {
    this.setState({ isSubmitting: true });
    this.props.registerUser(this.state.fullName, this.state.emailAddress, this.state.password);
  }

  showError(message) {
    const alert = this.errorAlert.current;
    alert.textContent = message || 'Unexpected error occurred. Please try again.';
    alert.style.display = 'block';
    this.setState({ isSubmitting: false });
  }

  onTermsCheckboxChange(event) {
    if (event.target.checked) {
      this.showTermsModal();
    } else {
      this.setState({ termsAccepted: false });
    }
  }

  showTermsModal() {
    const modalElement = this.termsModalRef.current;
    if (!modalElement || !bootstrap) {
      return;
    }

    if (!this.bootstrapTermsModal) {
      this.bootstrapTermsModal = new bootstrap.Modal(modalElement);
      modalElement.addEventListener('hidden.bs.modal', this.onTermsModalHidden);
    }

    this.bootstrapTermsModal.show();
  }

  onTermsModalHidden() {
    if (this.bootstrapTermsModal) {
      try {
        this.bootstrapTermsModal.dispose();
      } catch (error) {
        // Modal DOM may already be gone.
      }
      this.bootstrapTermsModal = null;
    }

    this.cleanupTermsModalDom();
  }

  cleanupTermsModalDom() {
    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
    document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
  }

  hideTermsModal() {
    if (!this.bootstrapTermsModal) {
      this.cleanupTermsModalDom();
      return;
    }

    try {
      this.bootstrapTermsModal.hide();
    } catch (error) {
      this.onTermsModalHidden();
    }
  }

  acceptTerms() {
    this.setState({ termsAccepted: true }, this.hideTermsModal);
  }

  componentWillUnmount() {
    const modalElement = this.termsModalRef.current;
    if (modalElement) {
      modalElement.removeEventListener('hidden.bs.modal', this.onTermsModalHidden);
    }

    if (this.bootstrapTermsModal) {
      try {
        this.bootstrapTermsModal.dispose();
      } catch (error) {
        // Modal DOM may already be gone.
      }
      this.bootstrapTermsModal = null;
    }

    this.cleanupTermsModalDom();
  }

  onSubmit(event) {
    event.preventDefault();
  }

  render() {
    const errorStyle = {
      display: 'none'
    };

    const subscriptionPrice = MONTHLY_SUBSCRIPTION_PRICE_USD;

    return (
      <form onSubmit={this.onSubmit}>
        <div className="alert alert-danger" style={errorStyle} role="alert" ref={this.errorAlert}>
          Unexpected error occurred. Please try again.
        </div>
        {this.props.subscriptionCanceled &&
          <div className="alert alert-warning" role="alert">
            Subscription checkout was canceled. No account was created — please try again and complete the checkout process.
          </div>}
        {this.props.signupError &&
          <div className="alert alert-danger" role="alert">
            {this.props.signupError}
          </div>}
        <div className="card border-light shadow-sm mb-4">
          <div className="card-body p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <p className="mb-1 font-weight-bold">Jashsoft Monthly</p>
                <p className="mb-0 text-muted small">Portfolio management subscription</p>
              </div>
              <div className="text-right">
                <p className="mb-0 h4">${subscriptionPrice}</p>
                <p className="mb-0 text-muted small">per month</p>
              </div>
            </div>
          </div>
        </div>
        <InputField iconName="person" label="Full Name" type="text" value={this.state.fullName}
          placeholder="John Doe" statekey="fullName" onChange={this.updateValues} focus={true}
          validateFunction={this.nameValid} validationErrorText="Please enter a name." />
        <InputField iconName="email" label="Email Address" type="email" value={this.state.emailAddress}
          placeholder="name@provider.com" statekey="emailAddress" onChange={this.updateValues}
          validateFunction={Utils.isValidEmail} validationErrorText="Please enter a valid E-mail address." />
        <InputField iconName="password" label="Password" type="password" value={this.state.password}
          placeholder="Password" statekey="password" onChange={this.updateValues}
          hintText="Must be at least 8 characters, and include Upper case, lower case, numbers and special characters."
          validateFunction={Utils.isValidPassword} validationErrorText="Please enter a valid Password." />
        <InputField iconName="password" label="Confirm Password" type="password" value={this.state.confirmPassword}
          placeholder="Confirm Password" statekey="confirmPassword" onChange={this.updateValues}
          validateFunction={this.confirmPasswordValid} validationErrorText="Password and confirm password should be the same." />
        <Checkbox text="I agree to the terms and conditions" checked={this.state.termsAccepted}
          onChange={this.onTermsCheckboxChange} />
        <ActionButton onClick={this.signupSubmit}
          disabled={!this.state.isFormValid || !this.state.termsAccepted || this.state.isSubmitting}
          text={this.state.isSubmitting ? 'Please wait...' : `Sign Up — $${subscriptionPrice}/month`}
          fullWidth={true} />
        <SecondaryButton onClick={this.props.showSignIn} text="Already an user? Sign In"/>
        <TermsModal modalRef={this.termsModalRef} onAccept={this.acceptTerms} />
      </form>
    );
  }
}

export default SignUpFields;
